'use client';

import { DocumentCard } from '@/components/ui/document-card';
import { Image } from '@/components/ui/image/image';
import type { MediaData } from '@/lib/getMediaData';
import { cn } from '@/lib/utils';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import styles from './media.module.scss';

const LottieComponent = dynamic(() => import('@/components/ui/card-with-image/lottie-component'), {
  ssr: false,
});

const RiveComponent = dynamic(() => import('@/components/ui/card-with-image/rive-component'), {
  ssr: false,
});

const HtmlEmbed = dynamic(
  () => import('@/components/ui/media/html-embed').then((m) => ({ default: m.HtmlEmbed })),
  { ssr: false },
);

export type MediaExperienceProps = {
  entryId?: string | null;
  className?: string;
};

const placeholder = (text: string) => (
  <div style={{ padding: '1rem', border: '1px dashed #ccc', borderRadius: 6, color: '#888' }}>
    {text}
  </div>
);

export const MediaExperience = ({ entryId, className }: MediaExperienceProps) => {
  const [media, setMedia] = useState<MediaData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stretch the Studio component wrapper so the media fills its column
  const stretchRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const wrapper = node.closest('[data-component-wrapper]');
    if (wrapper instanceof HTMLElement) {
      wrapper.style.width = '100%';
    }
  }, []);

  useEffect(() => {
    const trimmed = typeof entryId === 'string' ? entryId.trim() : '';
    if (!trimmed) return;
    let cancelled = false;

    fetch(`/api/media?id=${encodeURIComponent(trimmed)}`)
      .then((res) => {
        if (!res.ok)
          return res.text().then((t) => Promise.reject(new Error(`${res.status}: ${t}`)));
        return res.json();
      })
      .then((data: MediaData) => {
        if (!cancelled) setMedia(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
    };
  }, [entryId]);

  if (!entryId) return placeholder('Paste a Media entry ID.');
  if (error) return placeholder(`Error: ${error}`);
  if (!media) return placeholder('Loading…');

  const width = media.width ?? 800;
  const height = media.height ?? 500;

  const content = (() => {
    switch (media.mediaType) {
      case 'Advanced Image': {
        if (!media.src) return placeholder(`Image: no src. Raw data: ${JSON.stringify(media)}`);

        const img = <Image src={media.src} alt={media.alt ?? ''} width={width} height={height} />;

        if (media.url) {
          return (
            <a href={media.url} target="_blank" rel="noopener noreferrer">
              {img}
            </a>
          );
        }
        return img;
      }

      case 'Video': {
        if (!media.videoEmbedUrl)
          return placeholder(`Video: no embed URL. Raw data: ${JSON.stringify(media)}`);

        return (
          <div className={styles.wrapper}>
            <iframe
              className={styles.iframe}
              src={media.videoEmbedUrl}
              title="Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }

      case 'Animation': {
        if (!media.animationSrc && !media.lottieJson) {
          if (media.fallbackSrc) {
            return (
              <Image src={media.fallbackSrc} alt={media.alt ?? ''} width={width} height={height} />
            );
          }
          return null;
        }

        if (media.animationFormat === 'Rive' && media.animationSrc) {
          return (
            <div className={styles.rive} style={{ aspectRatio: `${width} / ${height}` }}>
              <RiveComponent
                src={media.animationSrc}
                autoplay={media.autoplay}
                playAudio={media.playAudio}
                width={width}
                height={height}
              />
            </div>
          );
        }

        if (media.lottieJson) {
          return (
            <LottieComponent data={media.lottieJson} loop={media.loop} autoplay={media.autoplay} />
          );
        }

        if (media.animationSrc) {
          return (
            <LottieComponent src={media.animationSrc} loop={media.loop} autoplay={media.autoplay} />
          );
        }
        return null;
      }

      case 'Embed': {
        if (!media.embedCode) return null;
        return <HtmlEmbed code={media.embedCode} />;
      }

      case 'Document': {
        if (!media.fileSrc || !media.fileName) return null;

        return (
          <DocumentCard
            fileSrc={media.fileSrc}
            fileName={media.fileName}
            fileType={media.fileType}
            fileSize={media.fileSize}
            description={media.description}
          />
        );
      }

      default:
        return null;
    }
  })();

  if (!content) return null;

  return (
    <div ref={stretchRef} className={cn(styles.root, className)}>
      {content}
    </div>
  );
};
